import React, { useState } from "react";
import { motion } from "framer-motion";
import { Text, View, Button } from "./base";
import { InputField } from "./base/InputField";
import { Icon } from "./base/Icon";
import { EmptyMessage } from "./EmptyMessage";
import { getColorForName } from "../utils/colors";

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const getTimestamp = (item: DataTableItem): string => {
  // Prefer lastActivityAt if it exists and is not null, otherwise use updatedAt, fallback to current time
  return item.lastActivityAt || item.updatedAt || new Date().toISOString();
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.02,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

type DataTableItem = {
  id: string;
  name: string;
  description: string;
  updatedAt?: string;
  lastActivityAt?: string | null;
};

type DataTableProps = {
  items: Record<string, unknown>[];
  entityName?: string;
  emptyMessage?: string;
  filter?: (searchQuery: string, item: Record<string, unknown>) => boolean;
  sort?: (a: Record<string, unknown>, b: Record<string, unknown>) => number;
  isLoading?: boolean;
  onClickCreate?: () => void;
  onRowClick?: (id: string) => void;
  showCreate?: boolean;
  showIcons?: boolean;
  iconName?: string; // Icon name to display in the dots
  timestampLabel?: string; // Custom label for the timestamp column
};

export const DataTable = ({
  items = [],
  entityName = "item",
  emptyMessage = `Create your first ${entityName} to get started.`,
  filter = () => true,
  sort = () => 0,
  isLoading = false,
  onClickCreate = () => {},
  onRowClick,
  showCreate = true,
  showIcons = false,
  iconName = "user",
  timestampLabel = "Last Updated",
}: DataTableProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<"name" | "updatedAt" | null>("updatedAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc"); // newest first by default

  const handleSort = (column: "name" | "updatedAt") => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedItems = items
    .filter((item) => filter(searchQuery, item))
    .sort((a, b) => {
      // Apply custom sorting if provided and no column sort is active
      if (!sortColumn) {
        return sort(a, b);
      }

      const aItem = a as DataTableItem;
      const bItem = b as DataTableItem;

      let comparison = 0;

      if (sortColumn === "name") {
        comparison = (aItem.name || "").localeCompare(bItem.name || "");
      } else if (sortColumn === "updatedAt") {
        comparison = new Date(getTimestamp(aItem)).getTime() - new Date(getTimestamp(bItem)).getTime();
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffMins < 60) {
      return diffMins <= 1 ? "1 minute ago" : `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
    } else if (diffDays < 7) {
      return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
    } else {
      return diffWeeks === 1 ? "1 week ago" : `${diffWeeks} weeks ago`;
    }
  };

  return (
    <View className="relative bg-white dark:bg-slate-900 px-4 sm:px-6 lg:px-8 pt-10 pb-0 min-h-full z-10">
      <div className="mb-8">
        <View className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <View className="relative flex-1">
            <InputField
              name="search"
              placeholder={`Search ${entityName}s by name or description...`}
              value={searchQuery}
              onChange={setSearchQuery}
              type="text"
              className="pl-10 w-full text-xs"
            />
            <Icon
              name="search"
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
          </View>
          {showCreate && (
            <Button
              onClick={onClickCreate}
              className="btn-primary w-full sm:w-auto"
            >
              <Icon
                name="add"
                size={16}
                className="mr-2"
              />
              <span className="hidden sm:inline">Create {capitalize(entityName)}</span>
              <span className="sm:hidden">Create</span>
            </Button>
          )}
        </View>
      </div>

      {isLoading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-center items-center py-20"
        >
          <View className="flex flex-col items-center gap-4">
            <motion.div
              animate={{
                rotate: 360,
                transition: { duration: 1.5, repeat: Infinity, ease: "linear" },
              }}
            >
              <Icon
                name="loader"
                size={36}
                className="text-indigo-500"
              />
            </motion.div>
            <Text className="text-gray-500 dark:text-gray-400 mt-4 animate-pulse">Loading your {entityName}s...</Text>
          </View>
        </motion.div>
      ) : sortedItems.length === 0 ? (
        <EmptyMessage
          title={`No ${capitalize(entityName)}s Found`}
          description={searchQuery === "" ? emptyMessage : `No ${entityName} match your search "${searchQuery}".\nTry a different search term.`}
          iconName="bot"
          buttonText={`Create ${capitalize(entityName)}`}
          onClickAdd={onClickCreate}
        />
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
        >
          {/* Table Header - Hidden on mobile */}
          <div className="hidden md:block bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-12 gap-4 px-6 py-3">
              <div
                className="col-span-4 font-medium text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                onClick={() => handleSort("name")}
              >
                Name
                {sortColumn === "name" && (
                  <Icon
                    name={sortDirection === "asc" ? "chevron-up" : "chevron-down"}
                    size={16}
                    className="text-gray-500 dark:text-gray-400"
                  />
                )}
              </div>
              <div className="col-span-6 font-medium text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Description</div>
              <div
                className="col-span-2 font-medium text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-1 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 justify-end"
                onClick={() => handleSort("updatedAt")}
              >
                {timestampLabel}
                {sortColumn === "updatedAt" && (
                  <Icon
                    name={sortDirection === "asc" ? "chevron-up" : "chevron-down"}
                    size={16}
                    className="text-gray-500 dark:text-gray-400"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {sortedItems.map((item, index) => {
              const typedItem = item as DataTableItem;
              return (
                <motion.div
                  key={typedItem.id}
                  variants={itemVariants}
                  className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                  onClick={() => onRowClick?.(typedItem.id)}
                >
                  {/* Desktop Layout */}
                  <div className="hidden md:grid grid-cols-12 gap-4">
                    <div className="col-span-4 flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full ${getColorForName(typedItem.name || typedItem.id)} flex-shrink-0 ${showIcons ? "flex items-center justify-center" : ""}`}
                      >
                        {showIcons && (
                          <Icon
                            name={iconName}
                            size={16}
                            className="text-white"
                          />
                        )}
                      </div>
                      <Text className={`text-sm font-medium text-gray-900 dark:text-white truncate ${!typedItem.name ? "italic" : ""}`}>
                        {typedItem.name || "Unnamed"}
                      </Text>
                    </div>
                    <div className="col-span-6 flex items-start py-1">
                      <Text className="text-xs text-gray-600 dark:text-gray-300">{typedItem.description || "—"}</Text>
                    </div>
                    <div className="col-span-2 flex items-center justify-end">
                      <Text className="text-xs text-gray-500 dark:text-gray-400">{formatDate(getTimestamp(typedItem))}</Text>
                    </div>
                  </div>

                  {/* Mobile Layout */}
                  <div className="md:hidden">
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-10 h-10 rounded-full ${getColorForName(typedItem.name || typedItem.id)} flex-shrink-0 ${showIcons ? "flex items-center justify-center" : ""}`}
                      >
                        {showIcons && (
                          <Icon
                            name={iconName}
                            size={18}
                            className="text-white"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Text className={`text-sm font-medium text-gray-900 dark:text-white ${!typedItem.name ? "italic" : ""}`}>
                          {typedItem.name || "Unnamed"}
                        </Text>
                        {typedItem.description && <Text className="text-xs text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{typedItem.description}</Text>}
                        <Text className="text-xs text-gray-500 dark:text-gray-400 mt-2">{formatDate(getTimestamp(typedItem))}</Text>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}
    </View>
  );
};
